/* eslint-disable camelcase */
/* eslint-disable no-nested-ternary */
import React from 'react';
import { useSelector } from 'react-redux';
import moment, { Moment } from 'moment';

import Modal from 'react-modal';
import DayPicker from 'react-day-picker';
import { TRootState } from '../../../../reducers';
import { TAdditionalService, TShiftData } from './AgentDelegateCard';
import { jobModes, matchingModeCodes, matchingModeLabels } from '../../../../consts';
import { DBS_CHECK_CHOICES, DBS_CHECK_LABELS } from '../../../../types';
import { findKeyByValue } from '../../../../utils';


type TModalConfirmShiftProps = {
    handleClose: () => void;
    handleFormSubmit: (data: any) => void;
    isOpen: boolean;
    mode: string;
    shiftData: TShiftData;
}

type TShiftDataRow = {
    key: string;
    label: string;
    value: string;
    valueStyle?: React.CSSProperties;
}

export function ModalConfirmShift({
    handleClose,
    handleFormSubmit,
    isOpen,
    mode,
    shiftData,
}: TModalConfirmShiftProps) {

    const {
        agents,
        servicesStructure,
        shiftClientCompanyName,
    } = useSelector((state: TRootState) => ({
        agents: state.agents.content,
        servicesStructure: state.servicesStructure,
        shiftClientCompanyName: state.clientData.basicInfo.company_name,
    }));

    const handleConfirm = (): void => {
        handleClose();
        handleFormSubmit(shiftData);
    };

    const getServiceRoleDisplay = (data: TShiftData | TAdditionalService): string => {
        const { service } = data;
        const fieldName = servicesStructure[service].role_field_name;
        const filterName = servicesStructure[service].role_filter_name;
        const role = data[fieldName];
        return `${servicesStructure[service].label} – ${servicesStructure[service][filterName][role].label}`;
    };

    const getShiftServiceAndRole = (): string => {
        const mainServiceDisplay = getServiceRoleDisplay(shiftData);
        if (shiftData.additional_services?.length) {
            const additionalServicesDisplay =
                shiftData.additional_services.map((service: TAdditionalService) => getServiceRoleDisplay(service));
            additionalServicesDisplay.unshift(mainServiceDisplay);
            return additionalServicesDisplay.join('\n');
        }
        return mainServiceDisplay;
    };

    const shiftAddress = [shiftData.address1, shiftData.postcode, shiftData.city];
    if (shiftData.address2) {
        shiftAddress.splice(1, 0, shiftData.address2);
    }

    const shiftDataRows: TShiftDataRow[] = [
        {
            key: 'shift-workers',
            label: 'Number of workers required',
            value: shiftData.number_of_freelancers,
        },
        {
            key: 'shift-client',
            label: 'Client',
            value: shiftClientCompanyName,
        },
        {
            key: 'shift-address',
            label: 'Address',
            value: shiftAddress.join(', '),
        },
        {
            key: 'shift-pay-rate',
            label: 'Pay rate',
            value: shiftData.freelancer_pay_per_hour ? `£${shiftData.freelancer_pay_per_hour}` : '',
        },
        {
            key: 'shift-bill-rate',
            label: 'Bill rate',
            value: shiftData.client_pay_per_hour ? `£${shiftData.client_pay_per_hour}` : '',
        },
        {
            key: 'shift-matching-mode',
            label: 'Matching mode',
            // @ts-ignore
            value: matchingModeLabels[findKeyByValue(matchingModeCodes, shiftData.matching_mode)],
        },
        {
            key: 'shift-consultant',
            label: 'Consultant',
            value: shiftData.consultant_splits?.map(split =>
                agents.find(agent => agent.id === split.agent)?.full_name).join(', ')
        },
        {
            key: 'shift-extra-rates',
            label: 'Extra rates',
            value: <i className={shiftData.rates?.length ? 'icon icon-check-12' : 'icon icon-close2'}/>,
        },
        {
            key: 'shift-dbs-check',
            label: 'DBS required',
            value: !shiftData.dbs_check || shiftData.dbs_check === DBS_CHECK_CHOICES.NO_DBS
                ? <i className='icon icon-close2'/>
                : DBS_CHECK_LABELS[shiftData.dbs_check],
        },
        {
            key: 'shift-qualifying-questions',
            label: 'Qualifying questions',
            value: <i className={shiftData.qualifying_questions?.length ? 'icon icon-check-12' : 'icon icon-close2'}/>,
        },
        {
            key: 'copy-existing-bookings',
            label: (!shiftData.bookings_to_duplicate || (shiftData.bookings_to_duplicate).length === 0)
                    ? 'Copy existing bookings'
                    : 'Selected workers',
            value: (!shiftData.bookings_to_duplicate || (shiftData.bookings_to_duplicate).length === 0)
                    ? <i className='icon icon-close2'/>
                    : (
                        shiftData.bookings?.map(booking => (
                            booking.status === 'OK'
                                ? booking.freelancer_detail.full_name
                                : null
                        )).filter(Boolean).join(', ')
                    ),
        },
    ];

    // prevents displaying the row when service and role/vehicle_type is missing (edit mode)
    if (shiftData.service && (shiftData.vehicle_type || shiftData.role)) {
        shiftDataRows.unshift({
            key: 'shift-service',
            label: 'Sector & Role',
            value: getShiftServiceAndRole(),
            valueStyle: { whiteSpace: 'pre-line' },
        });
    }

    const selectedDays: Date[] =
        // eslint-disable-next-line no-nested-ternary
        !shiftData.isMultiple
            ? [moment(shiftData.date).toDate()]
            : shiftData.dates
                ? shiftData.dates.map((date: string) => moment(date))
                    .sort((a: Moment, b: Moment) => a.diff(b))
                    .map((date: Moment) => date.toDate())
                : [];

    const modifiers =
        shiftData.isMultiple
            ? {
                'selected-right': (day: Date) =>
                    shiftData.dates.includes(moment(day).add(1, 'days').format('YYYY-MM-DD')),
                'selected-left': (day: Date) =>
                    shiftData.dates.includes(moment(day).add(-1, 'days').format('YYYY-MM-DD')),
            }
            : {};

    return (
        <Modal
            className="confirm-shift-modal"
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Confirm shift"
            ariaHideApp={false}
        >
            <h4>Please confirm the details below</h4>
            <div className="shift-data">
                <DayPicker
                    weekdaysShort={['S', 'M', 'T', 'W', 'T', 'F', 'S']}
                    selectedDays={selectedDays}
                    initialMonth={selectedDays[0]}
                    firstDayOfWeek={1}
                    fixedWeeks={true}
                    modifiers={modifiers}
                    numberOfMonths={3}
                    captionElement={({ date }: {date: Date}) => (
                        <div className="DayPicker-Caption">
                            {moment(date).format('MMMM')}
                        </div>
                    )}
                />
                <table className="shift-data-table">
                    <tbody>
                    {shiftDataRows.map(row =>
                            <tr key={row.key}>
                                <td className="right-col">{row.label}</td>
                                <td style={row.valueStyle}>{row.value}</td>
                            </tr>
                    )}
                    </tbody>
                </table>

                <div className="buttons-container">
                    <button
                        className="btn btn--round-small"
                        onClick={handleConfirm}
                        type="button"
                    >
                        {mode === jobModes.EDIT ? 'EDIT' : 'OPEN'}
                    </button>
                    <button
                        className="btn cancel-btn"
                        onClick={handleClose}
                        type="button"
                    >
                        CANCEL
                    </button>
                </div>
            </div>
        </Modal>
    );
}

type TConfirmShiftProps = {
    handleClose: () => void;
    handleFormSubmit: (data: any) => void;
    handleSubmit: (e: any) => void;
    isDisabled: boolean;
    isOpen: boolean;
    mode: string;
    shiftData: TShiftData,
}

export default function ConfirmShift({
    handleClose,
    handleFormSubmit,
    handleSubmit,
    isDisabled,
    isOpen,
    mode,
    shiftData,
}: TConfirmShiftProps) {

    return (
        <div className="modal-bottom-bar">
            <button
                className="table-action-button"
                type="button"
                disabled={isDisabled}
                onClick={handleSubmit}
            >
                {mode === jobModes.EDIT ? 'Edit job' : 'Open new job'}
            </button>

            {isOpen && (
                <ModalConfirmShift
                    handleClose={handleClose}
                    handleFormSubmit={handleFormSubmit}
                    isOpen={isOpen}
                    mode={mode}
                    shiftData={shiftData}
                />
            )}
        </div>
    );
}
